import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiShoppingCart } from "react-icons/fi";
import { FiSearch } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

export default function Menu() {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  const categories = ["all", "pizza", "burger", "extras", "drink", "deal"];
  const categoryLabels = {
    all: "All",
    pizza: "Pizzas",
    burger: "Burgers",
    extras: "Extras",
    drink: "Drinks",
    deal: "Deals",
  };
  // ----------------size labels------------------------
  const mobileSizeLabels = {
    sm: "S",
    md: "M",
    lg: "L",
    xl: "X",
  };

  const desktopSizeLabels = {
    sm: "SM",
    md: "MD",
    lg: "LG",
    xl: "XL",
  };

  // ===== Fade-up variant for cards =====
  const fadeUp = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  // ===== Skeleton loader that mimics ProductCard =====
  const SkeletonProductCard = () => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col h-full animate-pulse">
      <div className="aspect-square md:aspect-[4/4] bg-gray-200 rounded-t-2xl"></div>
      <div className="p-4 flex flex-col flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-3 bg-gray-200 rounded w-full"></div>
        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        <div className="flex gap-2 mt-3">
          <div className="h-6 w-10 bg-gray-200 rounded-full"></div>
          <div className="h-6 w-10 bg-gray-200 rounded-full"></div>
          <div className="h-6 w-10 bg-gray-200 rounded-full"></div>
        </div>

        <div className="flex gap-2 mt-auto">
          <div className="flex-1 h-8 bg-gray-200 rounded-lg"></div>
          <div className="flex-1 h-8 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    </div>
  );

  // ===== FETCH PRODUCTS =====
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(
          "https://supizza-backend.onrender.com/api/products",
        );
        const data = await res.json();
        setProducts(data);
      } catch (err) {
        console.error("Fetch failed:", err);
        toast.error("Failed to fetch products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // ===== Category from Navbar =====
  useEffect(() => {
    if (location.state?.category) {
      setCategory(location.state.category.toLowerCase());
    }
  }, [location.state]);

  // ===== FILTER PRODUCTS =====
  const filtered = useMemo(() => {
    return products.filter((item) => {
      const s = search.toLowerCase();
      const itemCategory = (item.category || "").toLowerCase().trim();
      return (
        (item.name.toLowerCase().includes(s) ||
          item.description.toLowerCase().includes(s) ||
          itemCategory.includes(s)) &&
        (category === "all" || itemCategory === category)
      );
    });
  }, [products, search, category]);

  // ===== ADD TO CART =====
  const addToCart = (product, selectedSize) => {
    const basePrice =
      selectedSize && product.sizes?.[selectedSize] !== undefined
        ? product.sizes[selectedSize]
        : product.price;

    const finalPrice =
      product.discount > 0
        ? Math.round(basePrice - (basePrice * product.discount) / 100)
        : basePrice;

    const newItem = {
      _id: product._id,
      name: product.name,
      image: product.image,
      size: selectedSize || null,
      unitPrice: finalPrice,
      quantity: 1,
    };

    const existingCart = JSON.parse(localStorage.getItem("cart")) || [];
    const existingIndex = existingCart.findIndex(
      (item) => item._id === newItem._id && item.size === newItem.size,
    );
    if (existingIndex !== -1) {
      existingCart[existingIndex].quantity += 1;
    } else {
      existingCart.push(newItem);
    }

    localStorage.setItem("cart", JSON.stringify(existingCart));
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success(`${product.name} added to cart`, {
      position: "bottom-right",
    });
  };

  // ===== PRODUCT CARD =====
  const ProductCard = ({ item }) => {
    const sizeOrder = ["sm", "md", "lg", "xl"];

    const sizeKeys =
      item.sizes && Object.keys(item.sizes).length > 0
        ? Object.keys(item.sizes).sort(
            (a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b),
          )
        : [];
    const defaultSize = sizeKeys.includes("md") ? "md" : sizeKeys[0];
    const [selectedSize, setSelectedSize] = useState(defaultSize);

    const basePrice =
      selectedSize && item.sizes && item.sizes[selectedSize] !== undefined
        ? item.sizes[selectedSize]
        : item.price;
    const finalPrice =
      item.discount > 0
        ? Math.round(basePrice - (basePrice * item.discount) / 100)
        : basePrice;

    return (
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col h-full"
      >
        <div className="aspect-square md:aspect-[4/4] bg-gray-50 flex items-center justify-center rounded-t-2xl">
          <img
            src={item.image}
            alt={item.name}
            className="max-h-36 md:max-h-44 object-contain"
          />
        </div>

        <div className="p-4 flex flex-col flex-1">
          <h2 className="text-xs    md:text-base font-semibold text-gray-900 line-clamp-2 capitalize leading-4 md:leading-5">
            {item.name}
          </h2>
          <p className=" hidden 2xl:block text-xs text-gray-400   line-clamp-2 mt-1">
            {item.description}
          </p>
          {sizeKeys.length > 0 && (
            <div className="flex gap-2 mt-3">
              {sizeKeys.map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  className={`
        flex items-center justify-center
        size-6 xl:w-auto lg:h-auto
        lg:px-3 lg:py-1
        text-[9px] xl:text-xs
        rounded-full border uppercase
        transition
        ${
          selectedSize === size
            ? "bg-red-600 text-white border-red-600"
            : "border-gray-300 text-gray-600 hover:border-red-400"
        }
      `}
                >
                  {/* Mobile */}
                  <span className="lg:hidden font-semibold flex justify-center items-center">
                    {mobileSizeLabels[size] || size}
                  </span>

                  {/* Desktop */}
                  <span className="hidden lg:inline">
                    {desktopSizeLabels[size] || size}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 mt-3 lg:mt-5">
            <div className="flex justify-center items-center gap-2">
              <span className="text-xs xl:text-sm font-semibold text-gray-900">
                Rs {finalPrice.toLocaleString("en-PK")}
              </span>
              {item.discount > 0 && (
                <span className=" flex  items-center  justify-center text-[10px] xl:text-xs text-gray-400 line-through">
                  Rs {basePrice.toLocaleString("en-PK")}
                </span>
              )}
            </div>
            {item.discount > 0 && (
              <div className="bg-red-100 text-red-700 rounded-md px-1 lg:px-2 text-xs lg:text-sm">
                {item.discount}%
              </div>
            )}
          </div>

          <div className="mt-auto flex gap-1  lg:gap-2 pt-2 md:pt-4">
            <button
              onClick={() =>
                navigate("/product/" + item._id, { state: { selectedSize } })
              }
              className="flex-1 border border-gray-300 text-xs lg:text-sm py-2 rounded-lg hover:bg-gray-100 transition"
            >
              Details
            </button>
            <button
              onClick={() => addToCart(item, selectedSize)}
              className="px-3 flex justify-center items-center bg-red-600 hover:bg-red-700  text-white py-2 rounded-lg transition"
            >
              <FiShoppingCart className="size-3 xl:size-4 2xl:size-5" />
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="bg-white min-h-screen max-w-7xl mx-auto px-6   py-20">
      <Toaster position="bottom-right" />

      <h1 className="text-4xl lg:text-5xl font-semibold text-center py-10 text-neutral-900">
        Menu
      </h1>

      {/* Search */}
      <div className="max-w-xl mx-auto relative mb-12">
        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          className="w-full pl-11 pr-4 py-3 rounded-md border border-gray-200 focus:border-neutral-900 outline-none"
        />
      </div>

      {/* Desktop categories */}
      <div className="hidden md:flex justify-center gap-8 mb-16">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            className={`text-sm pb-1 border-b transition ${
              category === cat
                ? "border-neutral-900 text-neutral-900"
                : "border-transparent text-gray-400 hover:text-neutral-900"
            }`}
          >
            {categoryLabels[cat]}
          </button>
        ))}
      </div>

      {/* Product grids */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {Array(8)
            .fill(0)
            .map((_, i) => (
              <SkeletonProductCard key={i} />
            ))}
        </div>
      ) : (
        categories
          .filter((c) => c !== "all")
          .map((cat) => {
            const items = filtered.filter(
              (item) => (item.category || "").toLowerCase().trim() === cat,
            );
            if (items.length === 0) return null;

            return (
              <div key={cat} className="mb-20">
                <h2 className="text-2xl font-semibold capitalize mb-8">
                  {categoryLabels[cat]}
                </h2>

                <motion.div
                  key={category} // ensures re-animation
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.05 } },
                  }}
                  className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
                >
                  <AnimatePresence>
                    {items.map((item) => (
                      <ProductCard key={item._id} item={item} />
                    ))}
                  </AnimatePresence>
                </motion.div>
              </div>
            );
          })
      )}

      {/* Mobile category bar */}
      <div className="md:hidden fixed left-1/2 bottom-6 transform -translate-x-1/2 bg-white/60 backdrop-blur-md border border-gray-200 rounded-full shadow-lg px-2 py-3  z-10">
        <div className="flex items-center   gap-1  md:gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`whitespace-nowrap px-3 font-semibold capitalize py-2 text-xs rounded-full transition ${
                category === cat
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {categoryLabels[cat]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
